/**
 * The second step the consent page asks for when the mint answered `401 assurance_required` for the authenticator
 * (plan-034 WAPP-APPSIGNIN; design §45.4 step 4, §45.10; AC-MOB-2).
 *
 * The organization's sign-in policy asks for the authenticator and this browser sign-in has not answered it. A person
 * who has an authenticator answers its code (or a recovery code) here — `answerMfaChallenge`, the sign-in's own second
 * step — and `onDone` mints again: the person is sent to the second step and back to the app without leaving the page.
 * A person who has none is told to add one in My security and start again from the app: the policy is met in the
 * portal, never around it, and no code is minted until it is.
 */
import { useMutation, useQuery } from "@tanstack/react-query";
import { ApiError } from "@wetalk/api-client";
import { Button, Field, Input } from "@wetalk/ui";
import { Link } from "@tanstack/react-router";
import { useState, type ReactNode } from "react";

import { useGateway } from "../../api/provider.js";
import "../../api/slice/personal-security.js";
import type { SecondStepFactor } from "../../api/slice/personal-security.js";
import { usePortalMessage } from "../../i18n/message.js";
import { portalText, usePortalTranslation } from "../../i18n/portal.js";

export function AppSignInSecondStep(props: { readonly onDone: () => void }): ReactNode {
  usePortalTranslation("appSignIn");
  const gateway = useGateway();
  const methods = useQuery({ queryKey: ["personal-security", "sign-in-methods", "app-sign-in"], queryFn: () => gateway.readMySignInMethods(), retry: false });
  const [factor, setFactor] = useState<SecondStepFactor>("totp");
  const [code, setCode] = useState("");
  const [problem, setProblem] = usePortalMessage();

  const answer = useMutation({
    mutationFn: () => gateway.answerMfaChallenge(factor, code.replace(/\s/g, "")),
    onSuccess: () => {
      setCode("");
      props.onDone();
    },
    onError: (raised: unknown) => {
      if (raised instanceof ApiError && raised.code === "mfa_factor_invalid") {
        setProblem(() => portalText("appSignIn", factor === "totp" ? "second_step.wrong_code" : "second_step.wrong_recovery"));
        return;
      }
      setProblem(() => portalText("appSignIn", "failed.lede"));
    },
  });

  if (methods.isPending) return <p className="pd-lede" role="status">{portalText("appSignIn", "loading")}</p>;
  if (methods.isError) {
    return (
      <>
        <p className="wt-field-error" role="alert">{portalText("appSignIn", "second_step.unreadable")}</p>
        <Button variant="secondary" block onClick={() => void methods.refetch()}>{portalText("appSignIn", "failed.try_again")}</Button>
      </>
    );
  }

  if (methods.data.totp.state !== "active") {
    return (
      <>
        <h1 tabIndex={-1}>{portalText("appSignIn", "enrol.title")}</h1>
        <p className="pd-lede">{portalText("appSignIn", "enrol.lede")}</p>
        <Link className="btn btn-primary btn-block" to="/me/security">{portalText("appSignIn", "enrol.open")}</Link>
      </>
    );
  }

  return (
    <form
      aria-labelledby="app-sign-in-second-step-title"
      onSubmit={(event) => {
        event.preventDefault();
        setProblem(null);
        if (code.trim() === "") {
          setProblem(() => portalText("appSignIn", factor === "totp" ? "second_step.empty_code" : "second_step.empty_recovery"));
          return;
        }
        answer.mutate();
      }}
    >
      <h1 id="app-sign-in-second-step-title" tabIndex={-1}>{portalText("appSignIn", "second_step.title")}</h1>
      <p className="pd-lede">{portalText("appSignIn", factor === "totp" ? "second_step.lede" : "second_step.recovery_lede")}</p>
      <Field label={portalText("appSignIn", factor === "totp" ? "second_step.code" : "second_step.recovery_code")} {...(problem === null ? {} : { error: problem })}>
        {(control) => (
          <Input
            {...control}
            name="appSignInSecondStepCode"
            inputMode={factor === "totp" ? "numeric" : undefined}
            autoComplete="one-time-code"
            value={code}
            onChange={(event) => {
              setCode(event.currentTarget.value);
            }}
          />
        )}
      </Field>
      <Button variant="primary" type="submit" block disabled={answer.isPending}>
        {portalText("appSignIn", answer.isPending ? "second_step.checking" : "second_step.continue")}
      </Button>
      <Button
        variant="ghost"
        block
        onClick={() => {
          setFactor(factor === "totp" ? "recovery_code" : "totp");
          setCode("");
          setProblem(null);
        }}
      >
        {portalText("appSignIn", factor === "totp" ? "second_step.use_recovery" : "second_step.use_app")}
      </Button>
    </form>
  );
}
